import { useDepartmentStore, type CampaignData } from "@/features/department";
import { buildBrandFieldPatch } from "@/features/brand/lib/brandCampaignData";
import { type DispatchContext } from "@/features/companion";
import { appEvents } from "@/shared/lib/events";

/**
 * Phase 10-7c cleanup: action returners for chat-driven regen
 * (`generate_brand_logos`, `generate_brand_icons`, `generate_brand_backgrounds`,
 * `generate_brand_assets`, `select_brand_logo`, `generate_brand_icon_manifest`,
 * `update_brand_icon_manifest`, `start_brand_research`, `regenerate_color_palette`)
 * were stripped from Remi v3 in migration 0028. The new flow is: Remi
 * redispatches the relevant `dispatch_brand_*_designer` and the sub-agent
 * self-persists the artifact via the image-gen tools. The remaining cases
 * here cover the still-live tools — write-throughs to wizard state that
 * sub-agents may still call (action returners, no-op for dispatch context).
 */

/** Returns true if the tool was handled. */
export function dispatchBrandTool(toolName: string, ctx: DispatchContext): boolean {
  const { payload, cpLog, source } = ctx;
  const state = useDepartmentStore.getState();

  switch (toolName) {
    case "update_brand_field": {
      const path = typeof payload.path === "string" ? payload.path : "";
      if (!path.trim()) {
        cpLog?.("brand-field-update-skip", `source=${source} reason=missing-path`);
        return true;
      }
      const patch = buildBrandFieldPatch(
        state.campaignData as Record<string, unknown>,
        path,
        payload.value,
      ) as Partial<CampaignData> | null;
      if (!patch) {
        cpLog?.("brand-field-update-skip", `source=${source} path=${path} reason=invalid-path`);
        return true;
      }
      state.updateCampaignData(patch);
      return true;
    }
    case "save_brand":
      appEvents.publish({
        type: "agent-save-brand",
        setActive: payload.setActive as unknown,
      });
      return true;
    default:
      return false;
  }
}
